/**
 * Date utility functions for crop schedule progress calculations
 */

/**
 * Parse PostgreSQL INTERVAL string to days
 * Handles formats like "30 days", "7 days", "2 months"
 *
 * @param {string} intervalString - PostgreSQL INTERVAL format (e.g., "30 days", "2 months")
 * @returns {number} Number of days
 */
export function parseIntervalToDays(intervalString) {
  if (!intervalString || typeof intervalString !== 'string') {
    console.warn('Invalid interval string:', intervalString)
    return 0
  }

  const trimmed = intervalString.trim().toLowerCase()

  // Match patterns like "30 days", "2 months", "1 year"
  const dayMatch = trimmed.match(/^(\d+)\s*days?$/)
  if (dayMatch) {
    return parseInt(dayMatch[1], 10)
  }

  const monthMatch = trimmed.match(/^(\d+)\s*months?$/)
  if (monthMatch) {
    // Approximate: 1 month = 30 days
    return parseInt(monthMatch[1], 10) * 30
  }

  const yearMatch = trimmed.match(/^(\d+)\s*years?$/)
  if (yearMatch) {
    // Approximate: 1 year = 365 days
    return parseInt(yearMatch[1], 10) * 365
  }

  console.warn('Unrecognized interval format:', intervalString)
  return 0
}

/**
 * Convert a date to an ISO date input value (YYYY-MM-DD).
 *
 * @param {string|Date} date - The date to format (Date object or parsable string).
 * @returns {string} ISO date string in YYYY-MM-DD format.
 */
export function toDateInputValue(date) {
  const d = new Date(date)
  return d.toISOString().split('T')[0]
}

/**
 * Add a specified number of days to a date string.
 *
 * @param {string|Date} dateStr - The base date (ISO string or Date object).
 * @param {number} days - Number of days to add.
 * @returns {string} ISO date string in YYYY-MM-DD format after adding the days.
 */
export function addDaysToDate(dateStr, days) {
  const d = new Date(dateStr)
  d.setDate(d.getDate() + days)
  return toDateInputValue(d)
}

/**
 * Calculate days elapsed between two dates
 *
 * @param {string|Date} startDate - Start date (ISO timestamp or Date object)
 * @param {string|Date} endDate - End date (defaults to current date/time)
 * @returns {number} Number of whole days elapsed (floored)
 */
export function getDaysElapsed(startDate, endDate = new Date()) {
  if (!startDate) {
    return 0
  }

  try {
    const start = startDate instanceof Date ? startDate : new Date(startDate)
    const end = endDate instanceof Date ? endDate : new Date(endDate)

    // Check for invalid dates
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.warn('Invalid date values:', { startDate, endDate })
      return 0
    }

    const millisecondsDiff = end.getTime() - start.getTime()
    const daysDiff = millisecondsDiff / (1000 * 60 * 60 * 24)

    // Return whole days (floored)
    return Math.floor(daysDiff)
  } catch (err) {
    console.error('Error calculating days elapsed:', err)
    return 0
  }
}

/**
 * Calculate phase progress percentage based on start date and estimated duration
 *
 * @param {string|Date} startedAt - When the phase actually started
 * @param {string} estimatedDuration - PostgreSQL INTERVAL string (e.g., "30 days")
 * @returns {number} Progress percentage (0-100, capped at 100)
 */
export function calculatePhaseProgress(startedAt, estimatedDuration) {
  if (!startedAt || !estimatedDuration) {
    return 0
  }

  try {
    const daysElapsed = getDaysElapsed(startedAt)
    const daysEstimated = parseIntervalToDays(estimatedDuration)

    if (daysEstimated <= 0) {
      return 0
    }

    const progressPercent = (daysElapsed / daysEstimated) * 100

    // Cap at 100% even if phase is overdue
    return Math.min(progressPercent, 100)
  } catch (err) {
    console.error('Error calculating phase progress:', err)
    return 0
  }
}

/**
 * Calculate weighted schedule progress based on phase durations
 * Completed phases contribute full weight, active phase contributes partial weight
 *
 * @param {Array} phases - Array of phase objects with is_complete, is_active, started_at, est_duration
 * @returns {number} Weighted progress percentage (0-100)
 */
export function calculateWeightedProgress(phases) {
  if (!phases || phases.length === 0) {
    return 0
  }

  try {
    let totalWeight = 0
    let completedWeight = 0

    for (const phase of phases) {
      const phaseWeight = parseIntervalToDays(phase.est_duration)
      totalWeight += phaseWeight

      if (phase.is_complete) {
        // Completed phases contribute full weight
        completedWeight += phaseWeight
      } else if (phase.is_active && phase.started_at) {
        // Active phase contributes partial weight based on progress
        const phaseProgress = calculatePhaseProgress(phase.started_at, phase.est_duration)
        completedWeight += phaseWeight * (phaseProgress / 100)
      }
      // Pending phases contribute 0
    }

    if (totalWeight <= 0) {
      return 0
    }

    return (completedWeight / totalWeight) * 100
  } catch (err) {
    console.error('Error calculating weighted progress:', err)
    return 0
  }
}

/**
 * Format date for display in tooltip
 * Uses locale-aware formatting for readable output
 *
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date string (e.g., "January 8, 2025")
 */
export function formatDate(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return ''
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date)
  } catch (err) {
    console.error('Error formatting date:', err)
    return ''
  }
}

/**
 * Format date for X-axis ticks (short format)
 * Compact format suitable for chart axes
 *
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date string (e.g., "Jan 2025")
 */
export function formatMonthYear(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return ''
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short'
    }).format(date)
  } catch (err) {
    console.error('Error formatting date:', err)
    return ''
  }
}

